import { $, _ } from "js/vendor";
import { getStaticUrl } from "js/config";
import { app } from "js/namespaces";
import * as geom from "js/core/utilities/geom";
import {
    SideBarCanvasModifier,
    ElementTextBlockPositionType, TrayType, HeaderPositionType, AssetType
} from "common/constants";
import EditorManager from "js/editor/elementUI/EditorManager";
import { SideBar } from "js/editor/ui";
import { ds } from "../../core/models/dataService";
import { deepClone } from "../../core/utilities/extensions";
import { ShowDialog, ShowDialogAsync } from "js/react/components/Dialogs/BaseDialog";
import BadFitDialog from "js/react/components/Dialogs/BadFitDialog";
import { AddAssetsContainer } from "js/react/views/AddAssets";

const AddContentPanel = SideBar.extend({
    className: "editor_menu add-content-panel",

    closeOnCanvasClick: true,
    preventCanvasEditingWhenOpen: true,
    showHeader: false,
    type: "add-content-panel",
    verticalGapOffset: 20,
    lockSlide: true,

    getPanelHeight: function() {
        return 110;
    },

    getCanvasModifier: function() {
        return SideBarCanvasModifier.SCALE;
    },

    getTitle: function() {
        return null;
    },

    renderContents: function($contents) {
        $contents.empty();

        let primaryElementType = app.currentCanvas.getPrimaryElement().type;
        let name = primaryElementType + "AddContentMenu";
        if (EditorManager.has(name)) {
            let editor = EditorManager.get(name);
            let props = new editor(app.currentCanvas.getPrimaryElement(), this);
            for (let panelProps of props) {
                $contents.addEl(this.renderCustomItems(panelProps));
            }
        }

        let $header = this.renderHeaderPanel();
        if ($header) {
            $contents.addEl($header);
        }

        let $footer = this.renderFooterPanel();
        if ($footer) {
            $contents.addEl($footer);
        }

        let $text = this.renderTextLayoutPanel();
        if ($text) {
            $contents.addEl($text);
        }

        let $trays = this.renderTrayLayoutPanel();
        if ($trays) {
            $contents.addEl($trays);
        }

        if ($contents.children().length == 0) {
            $contents.addEl($.label("No layout options"));
        }
    },

    renderThumbnail: function({ icon, label, enabled, selected, callback }) {
        let $thumbnail = $.div(`thumbnail ${icon}`);
        $thumbnail.append($.img(getStaticUrl(`/images/ui/trays/${icon}.png`)));
        $thumbnail.append($.div("frame"));
        // $thumbnail.append($.label(label));
        $thumbnail.toggleClass("disabled", enabled == false);
        $thumbnail.toggleClass("selected", selected == true);

        $thumbnail.on("click", () => {
            callback(!selected);
        });

        return $thumbnail;
    },

    renderCustomItems: function(props) {
        let $panel = $.div("panel");
        $panel.addEl($.label(props.title));
        let $options = $panel.addEl($.div("options"));

        for (let option of props.options) {
            $options.append(this.renderThumbnail(option));
        }

        return $panel;
    },

    renderHeaderPanel: function() {
        let $panel = $.div("panel");
        $panel.addEl($.label("Header"));
        let $options = $panel.addEl($.div("options"));
        let canvas = app.currentCanvas;

        let template = canvas.layouter.template;
        let layout = canvas.layouter.canvasElement;

        let renderHeaderOption = (position, enabled) => {
            let $option = this.renderThumbnail({
                icon: "header_" + position,
                label: position + " Header",
                enabled,
                selected: layout.headerPosition == position,
                callback: value => {
                    if (value) {
                        canvas.model.layout.headerPosition = position;
                    } else {
                        canvas.model.layout.headerPosition = HeaderPositionType.NONE;
                    }
                    canvas.markStylesAsDirty();
                    canvas.updateCanvasModel(true).then(() => {
                        this.render();
                    }).catch(err => {
                        ShowDialogAsync(BadFitDialog, {
                            title: `Sorry, the content on your slide won't fit with header on ${position}`,
                        });
                    });
                }
            });

            if (layout.trayLayout != TrayType.NONE && (position == HeaderPositionType.LEFT || position == HeaderPositionType.RIGHT)) {
                $option.addClass("disabled");
            }
            return $option;
        };

        if (template.allowHeader) {
            template.availableHeaderPositions.contains(HeaderPositionType.TOP) && $options.append(renderHeaderOption(HeaderPositionType.TOP, true));
            template.availableHeaderPositions.contains(HeaderPositionType.LEFT) && $options.append(renderHeaderOption(HeaderPositionType.LEFT, layout.trayLayout == TrayType.NONE));
        }

        if ($options.children().length == 0) {
            return null;
        }

        return $panel;
    },

    renderFooterPanel: function() {
        let $panel = $.div("panel");
        $panel.addEl($.label("Footer"));
        let $options = $panel.addEl($.div("options"));

        let canvas = app.currentCanvas;
        let template = canvas.layouter.template;
        let layout = canvas.layouter.canvasElement;

        if (template.allowFooter) {
            $options.append(this.renderThumbnail({
                icon: "footer",
                label: "Footer",
                enabled: canvas.layouter.canvasElement.canShowFooter,
                selected: layout.showFooter,
                callback: value => {
                    canvas.model.layout.showFooter = value ? "on" : "off";
                    canvas.updateCanvasModel(true).then(() => {
                        this.render();
                    }).catch(() => {
                        ShowDialogAsync(BadFitDialog, {
                            title: "Sorry, the content on your slide won't fit with footer",
                        });
                    });
                },
            }));
        }

        if ($options.children().length == 0) {
            return null;
        }

        return $panel;
    },

    renderTextLayoutPanel: function() {
        let $panel = $.div("panel");
        $panel.addEl($.label("Text Boxes"));
        let $options = $panel.addEl($.div("options"));

        let canvas = app.currentCanvas;
        let template = canvas.layouter.template;
        let layout = canvas.layouter.canvasElement.model.layout;

        if (template.allowElementTextInline) {
            const testModel = deepClone(canvas.model);
            testModel.layout.elementTextBlockPosition = ElementTextBlockPositionType.TRAY;
            const isFit = canvas.layouter.tryLayout(testModel);

            $options.append(this.renderThumbnail({
                icon: "bottom_inline_text",
                label: "Bottom Inline",
                enabled: isFit,
                selected: layout.elementTextBlockPosition == ElementTextBlockPositionType.INLINE,
                callback: value => {
                    if (value) {
                        canvas.model.layout.elementTextBlockPosition = ElementTextBlockPositionType.INLINE;
                    } else {
                        canvas.model.layout.elementTextBlockPosition = ElementTextBlockPositionType.NONE;
                    }
                    canvas.getCanvasElement().markStylesAsDirty();
                    canvas.updateCanvasModel(false).then(() => {
                        this.render();
                    }).catch(() => {
                        ShowDialogAsync(BadFitDialog, {
                            title: "Sorry, the content on your slide won't fit with an extra text block",
                        });
                    });
                },
            }));
        }
        if (template.allowElementTextTray) {
            const testModel = deepClone(canvas.model);
            testModel.layout.elementTextBlockPosition = ElementTextBlockPositionType.TRAY;
            const isFit = canvas.layouter.tryLayout(testModel);

            $options.append(this.renderThumbnail({
                icon: "bottom_tray_text",
                label: "Bottom Tray",
                enabled: isFit,
                selected: layout.elementTextBlockPosition == ElementTextBlockPositionType.TRAY,
                callback: value => {
                    if (value) {
                        canvas.model.layout.elementTextBlockPosition = ElementTextBlockPositionType.TRAY;
                    } else {
                        canvas.model.layout.elementTextBlockPosition = ElementTextBlockPositionType.NONE;
                    }
                    canvas.getCanvasElement().markStylesAsDirty();
                    canvas.updateCanvasModel(false).then(() => {
                        this.render();
                    }).catch(() => {
                        ShowDialogAsync(BadFitDialog, {
                            title: "Sorry, the content on your slide won't fit with an extra text tray",
                        });
                    });
                },
            }));
        }

        if (template.allowElementAttribution) {
            $options.append(this.renderThumbnail({
                icon: "attribution",
                label: "Footnote",
                enabled: true,
                selected: layout.showElementAttribution,
                callback: value => {
                    canvas.model.layout.showElementAttribution = value;
                    canvas.updateCanvasModel(true).then(() => {
                        this.render();
                    }).catch(() => {
                        ShowDialogAsync(BadFitDialog, {
                            title: "Sorry, the content on your slide won't fit with an attribution text block",
                        });
                    });
                },
            }));
        }

        let trayModel = canvas.model.elements.tray;
        let blockModel;
        if (trayModel && trayModel.items && trayModel.items.length) {
            blockModel = trayModel.items[0];
        }

        let setInlineText = (position, value) => {
            if (value) {
                canvas.model.layout.trayLayout = position;

                if (blockModel) {
                    if (blockModel.blocks == null || blockModel.blocks.length == 0) {
                        blockModel.blocks = [{ type: "body" }];
                    }
                } else {
                    canvas.model.elements.tray = {
                        items: [{
                            cellColor: "background_accent",
                            blocks: [{
                                type: "body"
                            }]
                        }]
                    };
                }
            } else {
                if (blockModel.content_value) {
                    blockModel.blocks = null;
                } else {
                    canvas.model.layout.trayLayout = TrayType.NONE;
                    canvas.model.elements.tray = null;
                }
            }
            canvas.updateCanvasModel(true).then(() => {
                this.render();
            }).catch(() => {
                ShowDialogAsync(BadFitDialog, {
                    title: "Sorry, the content on your slide won't fit with an extra image or text block",
                });
            });
        };

        if (template.availableTrayLayouts.contains(TrayType.LEFT_INLINE)) {
            $options.append(this.renderThumbnail({
                icon: "left_inline_text",
                label: "Left Text",
                enabled: layout.headerPosition != HeaderPositionType.LEFT,
                selected: layout.trayLayout == TrayType.LEFT_INLINE && blockModel && blockModel.blocks && blockModel.blocks.length,
                callback: value => setInlineText(TrayType.LEFT_INLINE, value),
            }));
        }
        if (template.availableTrayLayouts.contains(TrayType.RIGHT_INLINE)) {
            $options.append(this.renderThumbnail({
                icon: "right_inline_text",
                label: "Right Text",
                enabled: layout.headerPosition != HeaderPositionType.LEFT,
                selected: layout.trayLayout == TrayType.RIGHT_INLINE && blockModel && blockModel.blocks && blockModel.blocks.length,
                callback: value => setInlineText(TrayType.RIGHT_INLINE, value),
            }));
        }

        if ($options.children().length == 0) {
            return null;
        }

        return $panel;
    },

    // renderSideTrays: function() {
    //     let $panel = $.div("panel");
    //     $panel.addEl($.label("Images"));
    //     let $options = $panel.addEl($.div("options"));
    // },

    renderTrayLayoutPanel: function() {
        let $panel = $.div("panel");
        $panel.addEl($.label("Images"));
        let $options = $panel.addEl($.div("options"));

        let canvas = app.currentCanvas;
        let template = canvas.layouter.template;
        let layout = canvas.layouter.canvasElement;

        if (template.availableTrayLayouts.length == 0) {
            return null;
        }

        let trayContainer = canvas.layouter.elements.tray;
        let trayModel = canvas.model.elements.tray;
        let blockModel;
        let hasMedia = false;
        let hasText = false;
        if (trayModel && trayModel.items && trayModel.items.length) {
            blockModel = trayModel.items[0];
            hasMedia = blockModel.content_value != null;
            hasText = blockModel.blocks && blockModel.blocks.length != 0;
        }

        for (let trayLayout of template.availableTrayLayouts) {
            let icon = trayLayout;
            if (template.allowHeader && layout.headerPosition == HeaderPositionType.TOP) {
                icon += "_pict";
            } else {
                icon += "_full";
            }

            $options.append(this.renderThumbnail({
                icon,
                label: trayLayout.split("_")[0].toTitleCase(),
                enabled: layout.headerPosition != HeaderPositionType.LEFT,
                selected: layout.trayLayout == trayLayout && hasMedia,
                callback: value => {
                    if (value) {
                        // set the new tray layout
                        canvas.model.layout.trayLayout = trayLayout;

                        if (canvas.tempTrayModel) {
                            if (!blockModel) {
                                canvas.model.elements.tray = _.cloneDeep(canvas.tempTrayModel);
                                trayModel = canvas.model.elements.tray;
                                blockModel = trayModel.items[0];
                                hasMedia = true;
                            }
                        }

                        if (!trayModel) {
                            canvas.model.elements.tray = {};
                            trayModel = canvas.model.elements.tray;
                        }

                        if (!hasMedia) {
                            ShowDialog(AddAssetsContainer, {
                                assetType: AssetType.IMAGE,
                                workspaceId: ds.selection.presentation.getWorkspaceId(),
                                callback: model => {
                                    if (!blockModel) {
                                        canvas.model.elements.tray.items = [{}];
                                    }

                                    _.merge(
                                        canvas.model.elements.tray.items[0],
                                        model,
                                        {
                                            flipHorizontal: false,
                                            aoiLeft: null,
                                            aoiRight: null,
                                            aoiTop: null,
                                            aoiBottom: null,
                                            scale: null,
                                        },
                                    );

                                    canvas.updateCanvasModel(false).then(() => {
                                        this.render();
                                    }).catch(() => {
                                        ShowDialogAsync(BadFitDialog, {
                                            title: "Sorry, the content on your slide won't fit with an extra image or text block",
                                        });
                                    });
                                },
                            });
                        }

                        canvas.updateCanvasModel(true).then(() => {
                            this.render();
                        }).catch(() => {
                            ShowDialogAsync(BadFitDialog, {
                                title: "Sorry, the content on your slide won't fit with an extra image or text block",
                            });
                        });
                    } else {
                        // store current tray model in temp so we can restore if the user adds the tray back
                        canvas.tempTrayModel = _.cloneDeep(canvas.model.elements.tray);

                        if (hasText) {
                            blockModel.content_value = null;
                            blockModel.content_type = null;
                        } else {
                            // remove tray model
                            canvas.model.elements.tray = null;
                            canvas.model.layout.trayLayout = TrayType.NONE;
                        }

                        canvas.updateCanvasModel(true).then(() => {
                            this.render();
                        }).catch(() => {
                            ShowDialogAsync(BadFitDialog, {
                                title: "Sorry, the content on your slide won't fit with an extra image or text block",
                            });
                        });
                    }
                },
            }));
        }

        // don't allow trays when header is on left
        if (layout.headerPosition == HeaderPositionType.LEFT) {
            $panel.addClass("disabled");
            $panel.append($.div("warning", "Not available with left header"));
        }

        return $panel;
    },

});

export default AddContentPanel;
